import { useCallback } from "react";

import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { ContactsAnalyticsService } from "@analytics/index";

import copyToClipboard from "../utilities/copyToClipboard";

interface IEmailCellProperties {
  email?: null | string;
  emailStatus: string;
  fetchEmail: () => void;
  isFetching: boolean;
}

/**
 * Renders an EmailCell component.
 *
 * @param  email - The email string.
 * @param  emailStatus - The status of the email.
 * @param  fetchEmail - The function to fetch the email.
 * @param  isFetching - Indicates if the email is currently being fetched.
 */
function EmailCell({ email = null, emailStatus, fetchEmail, isFetching }: IEmailCellProperties) {
  const handleCopyEmail = useCallback(() => {
    if (email) {
      copyToClipboard(email, "Email");
      ContactsAnalyticsService.trackContactEmailCopied();
    }
  }, [email]);

  return (
    <Box alignItems="center" display="flex">
      {(email && email !== "email_not_unlocked@domain.com") || emailStatus === "unavailable" ? (
        <>
          {(emailStatus === "verified" || emailStatus === "extrapolated") && (
            <Tooltip title="Copy email to clipboard.">
              <IconButton onClick={handleCopyEmail} size="small">
                <ContentCopyRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="subtitle2">{emailStatus === "unavailable" ? "-" : email}</Typography>
        </>
      ) : (
        <Tooltip title="Get individual email for contact.">
          <span>
            <Button
              color="secondary"
              disabled={isFetching}
              endIcon={isFetching && <CircularProgress size={12} />}
              onClick={fetchEmail}
              size="small"
              variant="contained"
            >
              Get Email
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

export default EmailCell;
